import TableContainer from "@mui/material/TableContainer";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { Fragment } from "react";
import { LinkButton } from "../../components/LinkButton";
import * as yup from "yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { StagePicker } from "../../components/StagePicker";
import { UPDATE_BOOKINGS } from "./graphql";
import { UTCDateTimePicker } from "../../components/UTCDateTimePicker";
import { ErrorField } from "../../components/ErrorField";
import { ErrorRenderer } from "../../components/ErrorRenderer";

import type { EditableBand } from ".";

interface IBandTableProps {
  bands: EditableBand[];
  groupBy?: "stage" | "announcement";
}

const SUPPRESS_AUTO_EXPORT_TOAST_CONTEXT = {
  options: { suppressToast: true },
  skipAutoExportMessage: true,
};

const timeOrNull = yup
  .string()
  .transform((value) => (value === "" ? null : value))
  .nullable()
  .notRequired();

const bandSchema = yup
  .object({
    id: yup.string().required(),
    bookingId: yup.string().required(),
    band: yup
      .object({
        id: yup.string().required(),
        name: yup.string().required(),
      })
      .required(),
    startTime: timeOrNull,
    endTime: timeOrNull,
    stage: yup
      .object({
        id: yup.string().nullable(),
        name: yup.string().nullable(),
      })
      .nullable(),
    source: yup.string().nullable(),
  })
  .test("times-consistent", "", function (value) {
    const { startTime, endTime } = value || {};
    const pathPrefix = this.path ? `${this.path}.` : "";

    if (!startTime && !endTime) return true;

    if (startTime && !endTime) {
      return this.createError({
        path: `${pathPrefix}endTime`,
        message: "End time is required when start time is set",
      });
    }

    if (!startTime && endTime) {
      return this.createError({
        path: `${pathPrefix}startTime`,
        message: "Start time is required when end time is set",
      });
    }

    if (!dayjs(endTime).isAfter(dayjs(startTime))) {
      return this.createError({
        path: `${pathPrefix}endTime`,
        message: "End time must be after start time",
      });
    }

    return true;
  });

const schema = yup
  .object({
    bands: yup.array().of(bandSchema).required(),
  })
  .required();

const getErrorMessage = (error: unknown): string | undefined => {
  if (!error) return undefined;
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return undefined;
};

const BandsTable = ({ bands, groupBy }: IBandTableProps) => {
  const [updateBookings, { loading: updateLoading, error }] = useMutation(
    UPDATE_BOOKINGS,
    {
      onCompleted: () => {
        toast("Update complete");
      },
      onError: (error) => {
        toast(error.message);
      },
    },
  );
  const onSubmit = (val: any) => {
    updateBookings({
      variables: {
        bandBookings: val.bands.map((item: EditableBand) => {
          const { startTime, endTime } = item;
          const selectedStartTime = dayjs.utc(startTime);

          // const dates = updateZonedBookingTimes(startTime, endTime);
          const date = new Date(
            Date.UTC(
              selectedStartTime!.year(),
              selectedStartTime!.month(),
              selectedStartTime!.date(),
            ),
          );

          return {
            id: item.id,
            date: startTime && date,
            startTime: startTime && startTime,
            endTime: endTime && endTime,
            // ...dates,
            stageId: item.stage?.id,
          };
        }),
      },
      context: SUPPRESS_AUTO_EXPORT_TOAST_CONTEXT,
    });
  };
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<{ bands: EditableBand[] }>({
    // @ts-ignore
    resolver: yupResolver(schema),
    defaultValues: { bands },
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { fields } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: "bands", // unique name for your Field Array
  });

  let previousGroupKey: string | null = null;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Button
          type="submit"
          variant="contained"
          style={{ marginTop: 18, marginBottom: 18 }}
        >
          Save
        </Button>

        <ErrorRenderer errors={errors} />
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">name</TableCell>
                <TableCell align="left">start</TableCell>
                <TableCell align="left">end</TableCell>
                <TableCell align="left">duration</TableCell>
                <TableCell align="left">stage</TableCell>
                <TableCell align="left">announcement</TableCell>
                <TableCell align="left">actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((row, index) => {
                const rowData = row as unknown as EditableBand;
                const rowErrors = errors.bands?.[index];
                const rowMessage = getErrorMessage(rowErrors);
                const startTimeError =
                  getErrorMessage(rowErrors?.startTime) ?? rowMessage;
                const endTimeError =
                  getErrorMessage(rowErrors?.endTime) ?? rowMessage;
                const startTimeCellSx = startTimeError
                  ? {
                      borderLeft: "4px solid rgba(211, 47, 47, 0.85)",
                      backgroundColor: "rgba(211, 47, 47, 0.12)",
                      boxShadow: "inset 0 0 0 2px rgba(211, 47, 47, 0.2)",
                    }
                  : undefined;
                const endTimeCellSx = endTimeError
                  ? {
                      borderLeft: "4px solid rgba(211, 47, 47, 0.85)",
                      backgroundColor: "rgba(211, 47, 47, 0.12)",
                      boxShadow: "inset 0 0 0 2px rgba(211, 47, 47, 0.2)",
                    }
                  : undefined;
                const groupKey =
                  groupBy === "announcement"
                    ? rowData.source && rowData.source.trim().length > 0
                      ? rowData.source
                      : "Edition"
                    : (rowData.stage?.name ?? "No Stage");
                const showGroupHeader =
                  !!groupBy && groupKey !== previousGroupKey;
                if (showGroupHeader) {
                  previousGroupKey = groupKey;
                }

                return (
                  <Fragment key={row.id}>
                    {showGroupHeader && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          }}
                        >
                          {groupBy === "announcement"
                            ? `Announcement: ${groupKey}`
                            : groupKey}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell align="left">{row.band.name}</TableCell>
                      <TableCell align="left" sx={startTimeCellSx}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingTop: 16.5,
                          }}
                        >
                          <Controller
                            render={({ field }) => {
                              const value = field.value
                                ? dayjs(field.value)
                                : null;

                              return (
                                <UTCDateTimePicker
                                  format="YYYY/MM/DD HH:mm"
                                  ampm={false}
                                  timezone="UTC"
                                  label="Start Time"
                                  {...field}
                                  value={value}
                                  onChange={(newValue: Dayjs | null) =>
                                    field.onChange(
                                      newValue ? newValue.toISOString() : null,
                                    )
                                  }
                                  sx={{ minWidth: 160, flexShrink: 0 }}
                                />
                              );
                            }}
                            name={`bands.${index}.startTime`}
                            control={control}
                          />
                          <ErrorField>
                            {startTimeError}
                          </ErrorField>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={endTimeCellSx}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingTop: 16.5,
                          }}
                        >
                          <Controller
                            render={({ field }) => {
                              const value = field.value
                                ? dayjs(field.value)
                                : null;

                              return (
                                <UTCDateTimePicker
                                  format="YYYY/MM/DD HH:mm"
                                  ampm={false}
                                  timezone="UTC"
                                  label="End Time"
                                  {...field}
                                  value={value}
                                  onChange={(newValue: Dayjs | null) =>
                                    field.onChange(
                                      newValue ? newValue.toISOString() : null,
                                    )
                                  }
                                  sx={{ minWidth: 220, flexShrink: 0 }}
                                />
                              );
                            }}
                            name={`bands.${index}.endTime`}
                            control={control}
                          />
                          <ErrorField>
                            {endTimeError}
                          </ErrorField>
                        </div>
                      </TableCell>
                      <TableCell align="left">{row.duration}</TableCell>
                      <TableCell align="left">
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Controller
                            render={({ field }) => (
                              // @ts-ignore
                              <StagePicker
                                {...field}
                                value={field.value}
                                handleChange={(newValue: string) =>
                                  field.onChange(newValue)
                                }
                              />
                            )}
                            name={`bands.${index}.stage.id`}
                            control={control}
                          />
                        </div>
                      </TableCell>
                      <TableCell align="left">
                        {rowData.source && rowData.source.trim().length > 0
                          ? rowData.source
                          : "Edition"}
                      </TableCell>
                      <TableCell align="left">
                        <LinkButton
                          // @ts-ignore
                          path={`/booking/${row.bookingId}`}
                          text="View"
                        />
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </form>
    </>
  );
};

export default BandsTable;
